import BadgeFactory from '@/app/components/badge/badge-factory';
import { type LauncherUrlProps } from '@/utils/url/launcher-url';
import { GameUrl } from '@/app/components/game/subcomponents/game-url/game-url';
import { GameDetails } from '../../subcomponents/details/game-details';
import { type BadgeType } from '@/types/badges';
import { classes } from '@/utils/classes/classes';
import { SwjJackpotTicker } from '@/app/components/site-wide-jackpot/subcomponents/jackpot-ticker';
import { InfoButton } from '../../subcomponents/info-button';
import { gameImageSrcSets } from '@/utils/images/attributes';
import dynamic from 'next/dynamic';

import './styles/@theme';
import { PRISMIC_ZONE_LAYOUT } from '@/app/constants/zone';
import { SwjBorderOverlay } from '../../subcomponents/swj-border-overlay/swj-border-overlay';

const AnimatedTile = dynamic(() =>
    import('../../subcomponents/animated-tile/animated-tile').then((mod) => mod.AnimatedTile)
);

type GamePropsUI = {
    uid: string;
    name: string;
    description: string;
    launcherUrlProps: LauncherUrlProps;
    logoUrl: string;
    backgroundUrl: string;
    customBackgroundColor: string;
    label?: string;
    uniquePlayerCount?: number;
    isPopup: boolean;
    feedsCode?: string;
    hideGameInfo?: boolean;
    hasFavouriteButton?: boolean;
    priority?: boolean;
    gridLayout?: PRISMIC_ZONE_LAYOUT;
    swjLabel?: 'default' | 'small';
    isSiteWideJackpot?: boolean;
    forceTextBadge?: boolean;
    tracking?: {
        gameUid: string;
        gamePosition: number | null;
        gameName: string;
        gameProvider: string;
        rankOrder?: number | string | null;
        triggerGameName?: string | null;
        internalCode?: string | null;
    };
    triggerTrackEvent?: (gameId?: string | undefined) => void;
    animatedTileVideoUrl?: string | null;
};

export const Game = ({
    uid,
    name,
    description,
    launcherUrlProps,
    logoUrl,
    backgroundUrl,
    customBackgroundColor,
    label,
    uniquePlayerCount,
    feedsCode,
    swjLabel,
    isSiteWideJackpot,
    forceTextBadge = false,
    hideGameInfo = false,
    hasFavouriteButton = true,
    priority = false,
    tracking,
    triggerTrackEvent,
    animatedTileVideoUrl
}: GamePropsUI) => {
    const logoAndBackground = (
        <>
            {logoUrl?.length > 0 && (
                <img
                    width={300}
                    height={300}
                    className="logo"
                    src={logoUrl}
                    alt={`game-${name}-logo`}
                    loading={priority ? 'eager' : 'lazy'}
                    fetchPriority={priority ? 'high' : 'auto'}
                    {...gameImageSrcSets(logoUrl)}
                />
            )}
            {backgroundUrl?.length > 0 && (
                <img
                    width={300}
                    height={300}
                    className="background"
                    src={backgroundUrl}
                    alt={`game-${name}-background`}
                    loading={priority ? 'eager' : 'lazy'}
                    fetchPriority={priority ? 'high' : 'auto'}
                    {...gameImageSrcSets(backgroundUrl)}
                />
            )}
        </>
    );

    return (
        <div
            className={classes('game', hideGameInfo && 'hideGameInfo')}
            data-gameuid={uid}
            data-t-gameuid={tracking?.gameUid}
            data-t-gameposition={tracking?.gamePosition}
            data-t-gamename={tracking?.gameName}
            data-t-gameprovider={tracking?.gameProvider}
            data-t-rankorder={tracking?.rankOrder}
            data-t-triggergame={tracking?.triggerGameName}
            data-t-internalcode={tracking?.internalCode}
            data-t-isswj={!!isSiteWideJackpot}
            data-t-game-tile-type={animatedTileVideoUrl ? 'animated' : 'static'}>
            {label && (
                <BadgeFactory
                    badgeType={label as BadgeType}
                    feedsCode={feedsCode}
                    isSWJTickerDisplayed={isSiteWideJackpot}
                    forceTextBadge={forceTextBadge}
                    playerCount={uniquePlayerCount}
                />
            )}
            <InfoButton uid={uid} />
            <GameUrl
                gameUid={uid}
                gameName={name}
                launcherUrlProps={launcherUrlProps}
                triggerTrackEvent={triggerTrackEvent}>
                <div
                    className={classes('game__image-container', priority && 'priority')}
                    style={{ backgroundColor: customBackgroundColor }}>
                    {swjLabel && <SwjJackpotTicker isSmall={swjLabel === 'small'} />}
                    <SwjBorderOverlay isSiteWideJackpot={isSiteWideJackpot} swjLabel={swjLabel} />
                    {animatedTileVideoUrl ? (
                        <AnimatedTile videoSrc={animatedTileVideoUrl} gameName={name} priority={priority}>
                            {logoAndBackground}
                        </AnimatedTile>
                    ) : (
                        logoAndBackground
                    )}
                </div>
                <>
                    {!hideGameInfo && (
                        <GameDetails
                            uid={uid}
                            name={name}
                            description={description}
                            hasFavouriteButton={hasFavouriteButton}
                        />
                    )}
                </>
            </GameUrl>
        </div>
    );
};
